/**
 * useAuthFlowState — FW-126 giris akisinin dort karesi (email/password/pick/
 * done) AYRI ROTALARDIR (kabul kriteri 1); vue-router her gecişte bilesen
 * agacini unmount/mount eder. Adimlar arasi tasinmasi gereken veri (girilen
 * e-posta, girilen parola, login-by-email'den donen degisim jetonu + kiraci
 * listesi, secilen kiraci) bu yuzden route-local ref'lerde YASAYAMAZ — modul
 * kapsamli, tekil bir reactive nesnede tutulur.
 *
 * Kasitli olarak Pinia DEGIL: bu repoda henuz Pinia yok (olculdu — hicbir
 * package.json'da bagimlilik yok) ve tek bir yuzeyin (auth) kisa omurlu
 * sihirbaz durumu icin yeni bir state-yonetim katmani eklemek "Brownfield
 * clause" (frontend-vue skill) ihlali olurdu: "Pinia/axios kod tabanina
 * HANDOVER'a yazilmis bir karar olmadan ikinci veri katmani sokma". Vue'nin
 * kendi reactive() bu olcek icin yeterli ve composables/ desenine uyuyor.
 *
 * PAROLA (DUZELTME TURU 2/2 — bkz. useLogin.ts basligi): BFF `login` ucu
 * exchangeToken'i BILMIYOR, govdesi { tenantId(slug), email, password }
 * ister — bu yuzden parola PasswordView'dan PickView'a bu state uzerinden
 * tasinir. Tarayici depolama API lerine (kalici ya da oturumluk) ASLA
 * YAZILMAZ, yalniz bellekte reactive() icinde yasar.
 *
 * PAROLA OMRU (inceleme dongusu 1/2, bulgu 1): yukaridaki pencere SURESIZ
 * degildir. `clearPassword()` PickView'da `login.mutateAsync` her bittiginde
 * (basarili VEYA basarisiz — finally) VE akistan ayrilirken (route ayrilma
 * guard'i) cagrilir; bkz. PickView.vue basligi. Bu cagridan sonra deger
 * yalniz JS cop toplayicisinin isini bekler.
 *
 * NOT: bu yorumda o API lerin ADI bilerek yazilmiyor. FW-14 kabul kriteri 3
 * duz metin arayan bir grep ile olculuyor ve yorum ile kodu AYIRT ETMIYOR —
 * adi yazmak, dogru calisan kodu yanlis yere kirmizi gosteriyordu (olculdu).
 *
 * Sayfa yenilenirse (F5) bu durum kaybolur — BILEREK: parola/degisim jetonu
 * gibi hassas veriyi tarayici depolamasina YAZMAMA kurali (FW-14 kabul
 * kriteri 3) burada da gecerli. Guard'lar (routes.ts) yenilemeden sonra
 * kullaniciyi email adimina geri yollar.
 *
 * ROL (inceleme dongusu 2/2, bulgu — bkz. bu turun brief'i): `AuthFlowTenant`
 * eskiden bir `role` alani tasiyordu ama backend'in login-by-email/login
 * uclari (LoginByEmailModel.cs / LoginModel.cs) HICBIR ROL DONDURMUYOR — tip
 * yalan soyluyordu, `isOwner` uretimde daima false hesaplaniyordu. Rol yalniz
 * GET /api/v1/auth/session'dan (cerez yazildiktan, yani basarili login'den
 * SONRA) gelir; bu yuzden `isOwner` artik tenant'in bir alani DEGIL, akis
 * durumunun kendi alanidir ve PickView tarafindan session cagrisi bittikten
 * sonra `setIsOwner` ile yazilir (bkz. PickView.vue, useSession.ts).
 */
import { reactive, readonly, type DeepReadonly } from 'vue'

export interface AuthFlowTenant {
  readonly id: string
  readonly name: string
  readonly slug: string
}

interface AuthFlowStateData {
  email: string
  /** Bkz. dosya basligi "PAROLA" notu — bellek disina ASLA yazilmaz. */
  password: string | null
  exchangeToken: string | null
  tenants: AuthFlowTenant[]
  selectedTenantId: string | null
  /**
   * Kullanici auth'a bir App derin-baglantisindan dustuyse o yol (ornek:
   * '/expenses/42') — resolvePostLoginTarget'in ilk dali. EmailView giris
   * rotasinin `?from=` sorgu parametresinden yakalanir (bkz. EmailView.vue).
   */
  cameFromApp: string | null
  /**
   * Bkz. dosya basligi "ROL" notu — secilen calisma alaninda kullanicinin
   * Owner olup olmadigi. Varsayilan false'tur (guvenli varsayilan: bir hata
   * ya da eksik veri kullaniciyi asla yanlislikla Console'a sokmamali);
   * yalniz basarili bir GET /api/v1/auth/session yanitindan sonra true'ya
   * yazilir.
   */
  isOwner: boolean
  /**
   * FW-175 — 16-davet-mevcut: kullanici `/invite/{tenantId}/{token}` karesinden
   * "Evet — oturum açıp kabul edeceğim" ile STANDART giris akisina (email ->
   * password) girdiyse bu ucu InviteView'in basarili `peek()` cevabindan doldurulur.
   *
   * ONEMLI (tasarim gerekcesi): davet edilen kiraci login-by-email'in dondurdugu
   * `tenants` listesinde ARANAMAZ — o liste kullanicinin ZATEN uye oldugu
   * kiracilari tasir, davet edilen kiraci TANIM GEREGI orada degildir (uyelik
   * ancak accept-invitation BASARILI olunca kurulur). Bu yuzden PasswordView
   * basarili login-by-email sonrasi, bu alan doluysa: (1) accept-invitation'i
   * invitationToken ile cagirir, (2) basarili/basarisiz farketmeksizin BFF
   * login'i DOGRUDAN bu tenantId icin dener (selectTenant()), (3) basarili
   * olursa `addTenant()` ile bu kiraciyi `tenants` dizisine SONRADAN ekler
   * (DoneView/resolvePostLoginTarget onu orada bulsun diye — bkz. o dosyalarin
   * `tenants.find(...)` / `defaultTenantPath` mantigi). Herhangi bir adim
   * basarisiz olursa PasswordView sessizce normal 'pick' rotasina duser
   * (kullaniciyi asla dead-end'e sokmaz — bkz. PasswordView.vue basligi).
   *
   * Standart giris akisinda (davetsiz) HER UCU DE null kalir — bu yuzden
   * 06-tek-alan'in genel tek-kiraci atlamasindan (PasswordView'daki AYRI,
   * bagimsiz kontrol) farkli bir gerekcedir: burada "listede tek secenek var"
   * DEGIL, "baglamdan HANGI kiracinin hedeflendigi ONCEDEN BILINIYOR" sorusu
   * sorulur.
   */
  invitationTenantId: string | null
  invitationTenantName: string | null
  invitationToken: string | null
  /**
   * FW-175 (main yonlendirmesi 2026-09-16) — 15-davet-yeni-hesap'in e-posta
   * alani DUZENLENEBILIR (bkz. useRegisterStart.ts basligi — peek gercek
   * adresi asla vermez), ama tasarimin "🔒 ... değiştirilemez" ipucusunun
   * ANLAMINI korumak icin alanin UZERINDE peek'ten gelen MASKELI adres
   * ("Davet j***@***.com adresine gönderildi.") gosterilir — dogru VE
   * guvenli. InviteView'in basarili peek()'inden doldurulur.
   */
  invitationMaskedEmail: string | null
}

function createInitialState(): AuthFlowStateData {
  return {
    email: '',
    password: null,
    exchangeToken: null,
    tenants: [],
    selectedTenantId: null,
    cameFromApp: null,
    isOwner: false,
    invitationTenantId: null,
    invitationTenantName: null,
    invitationToken: null,
    invitationMaskedEmail: null,
  }
}

const state = reactive<AuthFlowStateData>(createInitialState())

export interface UseAuthFlowState {
  readonly state: DeepReadonly<AuthFlowStateData>
  setEmail(email: string): void
  setPassword(password: string): void
  /**
   * INCELEME DUZELTMESI (inceleme dongusu 1/2, bulgu 1) — parolanin bellekte
   * SURESIZ kalma penceresini sinirlar. PickView `login.mutateAsync`
   * bittiginde (basarili VEYA basarisiz) ve akistan ayrilirken cagirir
   * (bkz. PickView.vue). Tarayici depolamasina hic yazilmadigi icin bu
   * cagri sonrasi parola yalniz JS cop toplayicisini bekler.
   */
  clearPassword(): void
  setCameFromApp(path: string | null): void
  setCredentialsResult(exchangeToken: string, tenants: readonly AuthFlowTenant[]): void
  /** FW-175 — accept-invitation + BFF login basarili olduktan SONRA, davet
   * edilen kiraciyi `tenants` dizisine ekler (bkz. `invitationTenantId` alan
   * yorumu). Ayni id zaten varsa YENIDEN EKLENMEZ (idempotent). */
  addTenant(tenant: AuthFlowTenant): void
  selectTenant(tenantId: string): void
  /**
   * INCELEME DUZELTMESI (inceleme dongusu 2/2) — bkz. dosya basligi "ROL"
   * notu. PickView basarili login'in ARDINDAN GET /api/v1/auth/session'i
   * cagirip donen `roles` listesinden hesaplar ve burada yazar.
   */
  setIsOwner(isOwner: boolean): void
  /** FW-175 — bkz. `invitationTenantId`/`invitationToken` alan yorumlari.
   * `maskedEmail` opsiyonel (varsayilan null) — geriye donuk uyumluluk icin
   * (mevcut 3 argumanlik cagrilar bozulmasin). */
  setInvitationContext(
    tenantId: string | null,
    tenantName: string | null,
    token: string | null,
    maskedEmail?: string | null,
  ): void
  reset(): void
}

export function useAuthFlowState(): UseAuthFlowState {
  function setEmail(email: string): void {
    state.email = email
  }

  function setPassword(password: string): void {
    state.password = password
  }

  function clearPassword(): void {
    state.password = null
  }

  function setCameFromApp(path: string | null): void {
    state.cameFromApp = path
  }

  function setCredentialsResult(exchangeToken: string, tenants: readonly AuthFlowTenant[]): void {
    state.exchangeToken = exchangeToken
    state.tenants = [...tenants]
  }

  function addTenant(tenant: AuthFlowTenant): void {
    if (state.tenants.some((existing) => existing.id === tenant.id)) return
    state.tenants = [...state.tenants, tenant]
  }

  function selectTenant(tenantId: string): void {
    state.selectedTenantId = tenantId
  }

  function setIsOwner(isOwner: boolean): void {
    state.isOwner = isOwner
  }

  function setInvitationContext(
    tenantId: string | null,
    tenantName: string | null,
    token: string | null,
    maskedEmail: string | null = null,
  ): void {
    state.invitationTenantId = tenantId
    state.invitationTenantName = tenantName
    state.invitationToken = token
    state.invitationMaskedEmail = maskedEmail
  }

  function reset(): void {
    Object.assign(state, createInitialState())
  }

  return {
    state: readonly(state),
    setEmail,
    setPassword,
    clearPassword,
    setCameFromApp,
    setCredentialsResult,
    addTenant,
    selectTenant,
    setIsOwner,
    setInvitationContext,
    reset,
  }
}
